import { Color } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { useCallback } from 'react';
import styled from 'styled-components';

import FlagIssuesModal from 'dashboard/components/FlagIssuesModal';
import CopyInquiryLinkModal from 'dashboard/components/InquiryDetails/CopyInquiryLinkModal';
import ResumeInquiryModal from 'dashboard/components/InquiryDetails/ResumeInquiryModal';
import ReviewInquiryModal from 'dashboard/components/InquiryDetails/ReviewInquiryModal';
import SendInquiryEmailModal from 'dashboard/components/InquiryDetails/SendInquiryEmailModal';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned/Permissioned';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { ResumeInquiryFormData } from 'dashboard/forms/FormResumeInquiry';
import { SendInquiryEmailFormData } from 'dashboard/forms/FormSendInquiryEmail';
import { Document } from 'dashboard/models/document';
import { Environment, EnvironmentStatus } from 'dashboard/models/environment';
import { Inquiry, InquiryReview, InquiryStatus } from 'dashboard/models/inquiry';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import Tooltip from 'design-system/components/Tooltip';
import Link from 'images/icons/link.svg';
import IconMore from 'images/icons/more.svg';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { permissionErrorString } from 'lib/permissions-errors';

const Container = styled('div')``;

const Header = styled('div')`
  display: flex;
`;

const NavBackContainer = styled('div')`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const DisabledTooltipIconContainer = styled('div')`
  cursor: pointer;
  pointer-events: auto;

  svg path {
    fill: ${Color.CoreSlate700};
  }

  svg {
    vertical-align: middle;
  }
`;

interface InquiryHeaderProps {
  currentEnvironment: Environment;
  hideNavBack?: boolean;
  inquiry: Inquiry;
  verifications: Verification[];
  documents: Document[];
  resumeCode: string;
  subdomain: string;
  customDomain: string;
  currentUser: User;
  handlePrintInquiry: () => Promise<void>;
  handleFlagIssues: (values: FlagIssuesFormData) => Promise<void>;
  handleSubmitReview: (review: InquiryReview) => Promise<void>;
  redactInquiry: () => Promise<void>;
  resumeInquiry: (values: ResumeInquiryFormData) => Promise<void>;
  sendInquiryEmail: (values: SendInquiryEmailFormData) => Promise<void>;
  setResumeCode: (resumeCode: string) => void;
}

const InquiryHeader = ({
  currentEnvironment,
  hideNavBack = false,
  inquiry,
  verifications,
  documents,
  resumeCode,
  subdomain,
  customDomain,
  currentUser,
  handlePrintInquiry,
  handleFlagIssues,
  handleSubmitReview,
  redactInquiry,
  resumeInquiry,
  sendInquiryEmail,
  setResumeCode,
}: InquiryHeaderProps) => {
  const previewEnabled =
    currentEnvironment.type === ApiModel.EnvironmentSandbox &&
    inquiry.relationships?.['inquiry-template-version']?.data?.id != null;

  const [redactModal, { enable: showRedactModal, disable: hideRedactModal }] = useToggle(false);
  const [reviewModal, { enable: showReviewModal, disable: hideReviewModal }] = useToggle(false);
  const [shareInquiry, { enable: showShareInquiry, disable: hideShareInquiry }] = useToggle(false);
  const [
    showSendInquiryEmailModal,
    { enable: openSendInquiryEmailModal, disable: hideSendInquiryEmailModal },
  ] = useToggle(false);
  const [reportIssue, { enable: showReportIssue, disable: hideReportIssue }] = useToggle(false);
  const [
    showingResumeInquiry,
    { enable: showResumeInquiryModal, disable: hideResumeInquiryModal },
  ] = useToggle(false);

  const handleFlagIssuesAndClose = useCallback(
    async (values: FlagIssuesFormData) => {
      await handleFlagIssues(values);
      hideReportIssue();
    },
    [handleFlagIssues],
  );

  const handleResumeInquiry = useCallback(
    async (values: ResumeInquiryFormData) => {
      resumeInquiry(values);
    },
    [resumeInquiry],
  );

  const hideResumeInquiry = () => {
    hideResumeInquiryModal();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setResumeCode(null);
  };

  const cancelReview = useCallback(() => hideReviewModal(), []);

  const handleSubmitReviewAndClose = useCallback(
    async (review: InquiryReview) => {
      await handleSubmitReview(review);
      hideReviewModal();
    },
    [handleSubmitReview],
  );

  const handleSendInquiryEmail = useCallback(
    async (values: SendInquiryEmailFormData) => {
      await sendInquiryEmail(values);
      hideSendInquiryEmailModal();
    },
    [sendInquiryEmail],
  );

  const isRedacted = inquiry.attributes['redacted-at'] != null;

  const isResumable =
    !isRedacted &&
    (inquiry.attributes.status === InquiryStatus.Expired ||
      inquiry.attributes.status === InquiryStatus.Pending);
  const isCurrentEnvironmentActive =
    currentEnvironment.attributes.status === EnvironmentStatus.Active;
  const showShareButton = !isRedacted && inquiry.attributes.status === InquiryStatus.Created;

  const moreItems = [
    {
      id: 'inquiry-header__download-pdf',
      name: 'inquiry-header__download-pdf',
      'data-test': 'inquiry-header__download-pdf',
      onClick: handlePrintInquiry,
      label: 'Download PDF',
      disabled: !usePermission(Permission.InquiriesExport),
      disabledTooltip: permissionErrorString(Permission.InquiriesExport),
    },
    {
      id: 'inquiry-header__delete-inquiry',
      name: 'inquiry-header__delete-inquiry',
      'data-test': 'inquiry-header__delete-inquiry',
      onClick: showRedactModal,
      label: 'Redact inquiry',
      hidden: isRedacted,
      disabled: !usePermission(Permission.InquiryRedact),
      disabledTooltip: permissionErrorString(Permission.InquiryRedact),
      variant: 'danger' as const,
    },
    {
      id: 'inquiry-header__preview-inquiry',
      name: 'inquiry-header__preview-inquiry',
      onClick: () => window.open(`/dashboard/inquiries/${inquiry.id}/preview`, '_blank'),
      label: 'Preview',
      hidden: !previewEnabled,
      disabled: !usePermission(Permission.InquiryTemplateView),
      disabledTooltip: permissionErrorString(Permission.InquiryTemplateView),
    },
  ].filter((item) => ('hidden' in item ? !item.hidden : true));
  const shareItems = [
    {
      id: 'inquiry-header__copy-link',
      name: 'inquiry-show__copy-link',
      'data-test': 'inquiry-header__copy-link',
      onClick: showShareInquiry,
      label: 'Copy link',
    },
    {
      id: 'inquiry-header__send-inquiry-flow-email',
      name: 'inquiry-header__send-inquiry-flow-email',
      'data-test': 'inquiry-header__send-inquiry-flow-email',
      onClick: openSendInquiryEmailModal,
      label: 'Send email',
    },
  ];

  return (
    <Container>
      <Header>
        <NavBackContainer>
          {!hideNavBack && <NavBack to='/dashboard/inquiries'>All Inquiries</NavBack>}
        </NavBackContainer>
        <HStack>
          {Boolean(inquiry.attributes.decisionable) && (
            <Permissioned permission={[Permission.InquiryWrite, Permission.InquiryDecision]}>
              <Button
                id='inquiry-header__review-inquiry-button'
                data-test='inquiry-header__review-inquiry'
                onClick={showReviewModal}
                width='auto'
              >
                Review
              </Button>
            </Permissioned>
          )}
          {showShareButton && (
            <DropdownMenu
              trigger={
                <Button
                  tabIndex={0}
                  data-test='inquiry-header__share-inquiry'
                  variant='basic'
                  width='auto'
                  prefixIcon={<Link />}
                >
                  Share
                </Button>
              }
            >
              {shareItems.map((item) => (
                <DropdownMenu.Item
                  data-test={item['data-test']}
                  id={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    item.onClick();
                  }}
                  key={item.id}
                >
                  {item.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
          )}
          {isResumable && (
            <Button
              data-test='inquiry-header__resume-inquiry'
              variant='basic'
              width='auto'
              onClick={showResumeInquiryModal}
              disabled={!isCurrentEnvironmentActive}
            >
              Resume
            </Button>
          )}
          {currentEnvironment.type !== ApiModel.EnvironmentSandbox && (
            <Button
              data-test='inquiry-header__report-issue'
              variant='basic'
              width='auto'
              onClick={showReportIssue}
              disabled={!isCurrentEnvironmentActive}
            >
              Report issue
            </Button>
          )}
          <DropdownMenu
            trigger={
              <Button
                data-test='inquiry-header__button--dropdown'
                variant='basic'
                width='auto'
                prefixIcon={<IconMore />}
              >
                More
              </Button>
            }
            align='end'
          >
            {moreItems.map((item) => (
              <DropdownMenu.Item
                data-test={item['data-test']}
                id={item.id}
                key={item.id}
                variant={item.variant}
                disabled={item.disabled}
                onClick={item.onClick}
                suffixIcon={
                  item.disabled &&
                  item.disabledTooltip && (
                    <Tooltip
                      hideArrow
                      side='bottom'
                      trigger={
                        <DisabledTooltipIconContainer>
                          <CircleAlertIcon />
                        </DisabledTooltipIconContainer>
                      }
                      zIndex={Z_INDICES.toast + Z_INDICES.tooltip}
                      style={{ width: '350px' }}
                    >
                      {item.disabledTooltip}
                    </Tooltip>
                  )
                }
              >
                {item.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu>
        </HStack>
      </Header>

      <ModalWrapper isOpen={redactModal} handleCloseModal={hideRedactModal}>
        <ConfirmationModal
          cancelAction={hideRedactModal}
          confirmAction={redactInquiry}
          header='Redact this inquiry’s PII?'
          confirmActionText='Redact'
        >
          Personally identifiable information will be redacted. This action is permanent and can’t
          be undone.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={reviewModal} handleCloseModal={cancelReview}>
        <ReviewInquiryModal
          handleCancel={cancelReview}
          handleSubmitReview={handleSubmitReviewAndClose}
          inquiry={inquiry}
        />
      </ModalWrapper>

      <ModalWrapper isOpen={reportIssue} handleCloseModal={hideReportIssue}>
        <FlagIssuesModal
          verifications={verifications}
          documents={documents}
          handleClose={hideReportIssue}
          handleSubmitFlagIssues={handleFlagIssuesAndClose}
        />
      </ModalWrapper>

      <ModalWrapper isOpen={shareInquiry} handleCloseModal={hideShareInquiry}>
        <CopyInquiryLinkModal
          handleClose={hideShareInquiry}
          inquiry={inquiry}
          subdomain={subdomain}
          customDomain={customDomain}
        />
      </ModalWrapper>

      <ModalWrapper isOpen={showSendInquiryEmailModal} handleCloseModal={hideSendInquiryEmailModal}>
        <SendInquiryEmailModal
          handleCancel={hideSendInquiryEmailModal}
          handleSendInquiryEmail={handleSendInquiryEmail}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          emailAddress={
            currentEnvironment.type === ApiModel.EnvironmentSandbox
              ? currentUser.attributes['email-address']
              : inquiry.attributes['email-address']
          }
          currentEnvironment={currentEnvironment}
          expiresAt={inquiry.attributes['expires-at']}
        />
      </ModalWrapper>

      <ModalWrapper isOpen={showingResumeInquiry} handleCloseModal={hideResumeInquiry}>
        <ResumeInquiryModal
          expirySeconds={inquiry.attributes['expiration-after-resume-interval-seconds']}
          handleClose={hideResumeInquiry}
          handleResumeInquiry={handleResumeInquiry}
          subdomain={subdomain}
          customDomain={customDomain}
          resumeCode={resumeCode}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          emailAddress={
            currentEnvironment.type === ApiModel.EnvironmentSandbox
              ? currentUser.attributes['email-address']
              : inquiry.attributes['email-address']
          }
          currentEnvironment={currentEnvironment}
        />
      </ModalWrapper>
    </Container>
  );
};

export default InquiryHeader;
